import React, { useState, useEffect } from 'react';
import api from '../services/api';

const TABS = [
  { key: 'apps', label: 'Application', icon: '📱', commands: [
    { name: 'List Apps', cmd: 'cf apps', desc: 'List all applications' },
    { name: 'App Detail', cmd: 'cf app {APP_NAME}', desc: 'Show app details', params: ['APP_NAME'] },
    { name: 'Start', cmd: 'cf start {APP_NAME}', desc: 'Start an application', params: ['APP_NAME'] },
    { name: 'Stop', cmd: 'cf stop {APP_NAME}', desc: 'Stop an application', params: ['APP_NAME'], danger: true },
    { name: 'Restart', cmd: 'cf restart {APP_NAME}', desc: 'Restart an application', params: ['APP_NAME'] },
    { name: 'Restage', cmd: 'cf restage {APP_NAME}', desc: 'Restage an application', params: ['APP_NAME'] },
    { name: 'Rename', cmd: 'cf rename {OLD_APP} {NEW_APP}', desc: 'Rename an application', params: ['OLD_APP', 'NEW_APP'] },
    { name: 'Delete', cmd: 'cf delete {APP_NAME} -f', desc: 'Delete an application', params: ['APP_NAME'], danger: true },
  ]},
  { key: 'scaling', label: 'Scaling', icon: '📈', commands: [
    { name: 'Scale Instances', cmd: 'cf scale {APP_NAME} -i {INSTANCES}', desc: 'Scale app instances', params: ['APP_NAME', 'INSTANCES'] },
    { name: 'Scale Memory', cmd: 'cf scale {APP_NAME} -m {MEMORY}', desc: 'Scale app memory (e.g. 1G)', params: ['APP_NAME', 'MEMORY'] },
    { name: 'Scale Disk', cmd: 'cf scale {APP_NAME} -k {DISK}', desc: 'Scale app disk quota (e.g. 2G)', params: ['APP_NAME', 'DISK'] },
  ]},
  { key: 'logs', label: 'Logs', icon: '📋', commands: [
    { name: 'Recent Logs', cmd: 'cf logs {APP_NAME} --recent', desc: 'Show recent logs', params: ['APP_NAME'] },
    { name: 'Events', cmd: 'cf events {APP_NAME}', desc: 'Show app events', params: ['APP_NAME'] },
  ]},
  { key: 'routes', label: 'Routes', icon: '🌍', commands: [
    { name: 'List Routes', cmd: 'cf routes', desc: 'List all routes' },
    { name: 'Map Route', cmd: 'cf map-route {APP_NAME} {DOMAIN} --hostname {HOSTNAME}', desc: 'Map a route to an app', params: ['APP_NAME', 'DOMAIN', 'HOSTNAME'] },
    { name: 'Unmap Route', cmd: 'cf unmap-route {APP_NAME} {DOMAIN} --hostname {HOSTNAME}', desc: 'Unmap a route', params: ['APP_NAME', 'DOMAIN', 'HOSTNAME'] },
    { name: 'Delete Route', cmd: 'cf delete-route {DOMAIN} --hostname {HOSTNAME} -f', desc: 'Delete a route', params: ['DOMAIN', 'HOSTNAME'], danger: true },
  ]},
  { key: 'login', label: 'Login', icon: '🔐', commands: [
    { name: 'Target', cmd: 'cf target', desc: 'Show current target (org/space)' },
    { name: 'Set Target', cmd: 'cf target -o {ORG} -s {SPACE}', desc: 'Set target org and space', params: ['ORG', 'SPACE'] },
    { name: 'Logout', cmd: 'cf logout', desc: 'Logout from CF' },
  ]},
  { key: 'orgs', label: 'Orgs & Spaces', icon: '🏢', commands: [
    { name: 'List Orgs', cmd: 'cf orgs', desc: 'List all organizations' },
    { name: 'List Spaces', cmd: 'cf spaces', desc: 'List all spaces' },
    { name: 'Space Detail', cmd: 'cf space {SPACE_NAME}', desc: 'Show space details', params: ['SPACE_NAME'] },
  ]},
  { key: 'env', label: 'Environment', icon: '⚙️', commands: [
    { name: 'Show Env', cmd: 'cf env {APP_NAME}', desc: 'Show environment variables', params: ['APP_NAME'] },
    { name: 'Set Env', cmd: 'cf set-env {APP_NAME} {VAR_NAME} {VAR_VALUE}', desc: 'Set an environment variable', params: ['APP_NAME', 'VAR_NAME', 'VAR_VALUE'] },
    { name: 'Unset Env', cmd: 'cf unset-env {APP_NAME} {VAR_NAME}', desc: 'Remove an environment variable', params: ['APP_NAME', 'VAR_NAME'] },
  ]},
  { key: 'services', label: 'Services', icon: '🗄️', commands: [
    { name: 'List Services', cmd: 'cf services', desc: 'List all service instances' },
    { name: 'Service Detail', cmd: 'cf service {SERVICE_NAME}', desc: 'Show service details', params: ['SERVICE_NAME'] },
    { name: 'Create Service', cmd: 'cf create-service {SERVICE} {PLAN} {INSTANCE_NAME}', desc: 'Create a service instance', params: ['SERVICE', 'PLAN', 'INSTANCE_NAME'] },
    { name: 'Delete Service', cmd: 'cf delete-service {SERVICE_NAME} -f', desc: 'Delete a service instance', params: ['SERVICE_NAME'], danger: true },
    { name: 'Bind Service', cmd: 'cf bind-service {APP_NAME} {SERVICE_NAME}', desc: 'Bind service to app', params: ['APP_NAME', 'SERVICE_NAME'] },
    { name: 'Unbind Service', cmd: 'cf unbind-service {APP_NAME} {SERVICE_NAME}', desc: 'Unbind service from app', params: ['APP_NAME', 'SERVICE_NAME'] },
  ]},
  { key: 'servicekeys', label: 'Service Keys', icon: '🔑', commands: [
    { name: 'List Keys', cmd: 'cf service-keys {SERVICE_NAME}', desc: 'List service keys', params: ['SERVICE_NAME'] },
    { name: 'Show Key', cmd: 'cf service-key {SERVICE_NAME} {KEY_NAME}', desc: 'Show service key details', params: ['SERVICE_NAME', 'KEY_NAME'] },
    { name: 'Create Key', cmd: 'cf create-service-key {SERVICE_NAME} {KEY_NAME}', desc: 'Create a service key', params: ['SERVICE_NAME', 'KEY_NAME'] },
    { name: 'Delete Key', cmd: 'cf delete-service-key {SERVICE_NAME} {KEY_NAME} -f', desc: 'Delete a service key', params: ['SERVICE_NAME', 'KEY_NAME'], danger: true },
  ]},
  { key: 'tasks', label: 'Tasks', icon: '📂', commands: [
    { name: 'Run Task', cmd: 'cf run-task {APP_NAME} --command "{COMMAND}" --name {TASK_NAME}', desc: 'Run a task', params: ['APP_NAME', 'COMMAND', 'TASK_NAME'] },
    { name: 'List Tasks', cmd: 'cf tasks {APP_NAME}', desc: 'List tasks for an app', params: ['APP_NAME'] },
    { name: 'Terminate Task', cmd: 'cf terminate-task {APP_NAME} {TASK_ID}', desc: 'Terminate a task', params: ['APP_NAME', 'TASK_ID'] },
  ]},
  { key: 'ssh', label: 'SSH', icon: '💻', commands: [
    { name: 'Enable SSH', cmd: 'cf enable-ssh {APP_NAME}', desc: 'Enable SSH for an app', params: ['APP_NAME'] },
    { name: 'Disable SSH', cmd: 'cf disable-ssh {APP_NAME}', desc: 'Disable SSH for an app', params: ['APP_NAME'] },
  ]},
  { key: 'deploy', label: 'Blue-Green', icon: '🔄', commands: [
    { name: 'MTA Operations', cmd: 'cf dmol', desc: 'List active MTA operations' },
    { name: 'Resume Deploy', cmd: 'cf deploy -i {OPERATION_ID} -a resume', desc: 'Resume a deployment', params: ['OPERATION_ID'] },
    { name: 'Abort Deploy', cmd: 'cf deploy -i {OPERATION_ID} -a abort', desc: 'Abort a deployment', params: ['OPERATION_ID'], danger: true },
    { name: 'Retry Deploy', cmd: 'cf deploy -i {OPERATION_ID} -a retry', desc: 'Retry a deployment', params: ['OPERATION_ID'] },
  ]},
  { key: 'info', label: 'Info', icon: '📊', commands: [
    { name: 'Buildpacks', cmd: 'cf buildpacks', desc: 'List available buildpacks' },
    { name: 'Domains', cmd: 'cf domains', desc: 'List available domains' },
    { name: 'Plugins', cmd: 'cf plugins', desc: 'List installed plugins' },
    { name: 'Quota', cmd: 'cf quota {QUOTA_NAME}', desc: 'Show quota details', params: ['QUOTA_NAME'] },
    { name: 'Quotas', cmd: 'cf quotas', desc: 'List all quotas' },
  ]},
];

export default function CfApps() {
  const [activeTab, setActiveTab] = useState('apps');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastCommand, setLastCommand] = useState('');
  const [paramValues, setParamValues] = useState({});
  const [history, setHistory] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedEnv, setSelectedEnv] = useState('');

  useEffect(() => { api.get('/companies').then(r => setCompanies(r.data)); }, []);

  const currentTab = TABS.find(t => t.key === activeTab);
  const company = companies.find(c => c.id === Number(selectedCompany));

  const envBadge = (env) => {
    const colors = { dev: '#3b82f6', qas: '#f59e0b', prd: '#059669' };
    return env ? <span style={{ background: colors[env] || '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{env}</span> : null;
  };

  const getEnvInfo = () => {
    if (!company || !selectedEnv) return null;
    const e = selectedEnv;
    return {
      apiUrl: company[e + 'CfApiUrl'],
      org: company[e + 'CfOrg'],
      space: company[e + 'CfSpace'],
      username: company[e + 'CfUsername'],
      hasPassword: !!company[e + 'CfPassword'],
    };
  };

  const envInfo = getEnvInfo();

  const buildCommand = (cmd, params) => {
    let finalCmd = cmd;
    if (params) {
      params.forEach(p => {
        finalCmd = finalCmd.replace(`{${p}}`, paramValues[p] || '');
      });
    }
    return finalCmd;
  };

  const execute = async (cmdTemplate) => {
    if (!selectedCompany || !selectedEnv) {
      return alert('Please select a Company and Environment first');
    }
    const finalCmd = buildCommand(cmdTemplate.cmd, cmdTemplate.params);
    if (cmdTemplate.params && cmdTemplate.params.some(p => !paramValues[p])) {
      return alert('Please fill all required parameters');
    }
    if (cmdTemplate.danger && !window.confirm(`Run "${finalCmd}"? This action may be destructive.`)) return;

    setLoading(true);
    setLastCommand(finalCmd);
    setOutput(`Running: ${finalCmd}\nCompany: ${company?.name} | Env: ${selectedEnv.toUpperCase()}\n\n⏳ Logging in and executing...\n`);
    try {
      const { data } = await api.post('/cf/execute', {
        command: finalCmd,
        companyId: Number(selectedCompany),
        env: selectedEnv
      });
      const result = data.output || data.error || 'No output';
      setOutput(result);
      setHistory(prev => [{
        command: finalCmd,
        output: result,
        time: new Date().toLocaleTimeString(),
        company: company?.name,
        env: selectedEnv
      }, ...prev.slice(0, 29)]);
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message;
      setOutput('Error: ' + errMsg);
      setHistory(prev => [{
        command: finalCmd,
        output: 'Error: ' + errMsg,
        time: new Date().toLocaleTimeString(),
        company: company?.name,
        env: selectedEnv
      }, ...prev.slice(0, 29)]);
    }
    setLoading(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>CF Command Center</h1>
          <p style={{ color: '#64748b', fontSize: 14 }}>Execute Cloud Foundry commands per company environment</p>
        </div>
      </div>

      {/* Company & Environment Filter */}
      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Company</label>
            <select value={selectedCompany} onChange={e => { setSelectedCompany(e.target.value); setSelectedEnv(''); }}
              style={{ width: 220, fontSize: 13, padding: '8px 10px' }}>
              <option value="">-- Select Company --</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Environment</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {['dev', 'qas', 'prd'].map(env => {
                const colors = { dev: '#3b82f6', qas: '#f59e0b', prd: '#059669' };
                const isActive = selectedEnv === env;
                return (
                  <button key={env} onClick={() => setSelectedEnv(env)}
                    disabled={!selectedCompany}
                    style={{
                      padding: '8px 20px', fontSize: 13, fontWeight: 700, borderRadius: 6, border: 'none', cursor: selectedCompany ? 'pointer' : 'not-allowed',
                      background: isActive ? colors[env] : '#f1f5f9',
                      color: isActive ? '#fff' : '#64748b',
                      textTransform: 'uppercase', transition: 'all 0.15s',
                      opacity: selectedCompany ? 1 : 0.5
                    }}>{env}</button>
                );
              })}
            </div>
          </div>
          {envInfo && (
            <div style={{ fontSize: 12, color: '#6b7280', padding: '6px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
              <div><strong>API:</strong> {envInfo.apiUrl || <span style={{ color: '#ef4444' }}>Not configured</span>}</div>
              <div><strong>Org:</strong> {envInfo.org || '-'} &nbsp;|&nbsp; <strong>Space:</strong> {envInfo.space || '-'}</div>
              <div><strong>User:</strong> {envInfo.username || <span style={{ color: '#ef4444' }}>Not configured</span>}
                {envInfo.hasPassword ? <span style={{ color: '#059669' }}> ✓ Password set</span> : <span style={{ color: '#ef4444' }}> ✗ No password</span>}
              </div>
            </div>
          )}
          {selectedCompany && selectedEnv && envInfo && !envInfo.apiUrl && (
            <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 500 }}>
              ⚠ Configure {selectedEnv.toUpperCase()} CF credentials in Companies page first
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 20, padding: 4, background: '#f1f5f9', borderRadius: 10 }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setParamValues({}); }}
            style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer',
              background: activeTab === tab.key ? '#fff' : 'transparent',
              color: activeTab === tab.key ? '#1e293b' : '#64748b',
              boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s'
            }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Commands Panel */}
        <div className="card">
          <h3 style={{ marginBottom: 16, fontWeight: 600 }}>
            {currentTab.icon} {currentTab.label} Commands
            {selectedCompany && selectedEnv && <span style={{ marginLeft: 8 }}>{envBadge(selectedEnv)}</span>}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {currentTab.commands.map((cmd, i) => (
              <div key={i} style={{
                padding: 14, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc',
                opacity: (!selectedCompany || !selectedEnv) ? 0.6 : 1
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: cmd.params ? 10 : 0 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{cmd.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{cmd.desc}</div>
                    <code style={{ fontSize: 11, color: '#7c3aed', background: '#f5f3ff', padding: '2px 6px', borderRadius: 4, marginTop: 4, display: 'inline-block' }}>
                      {cmd.cmd}
                    </code>
                  </div>
                  <button
                    className={cmd.danger ? 'btn-danger' : 'btn-primary'}
                    style={{ padding: '6px 14px', fontSize: 12, whiteSpace: 'nowrap' }}
                    disabled={loading || !selectedCompany || !selectedEnv}
                    onClick={() => execute(cmd)}>
                    {loading && lastCommand === buildCommand(cmd.cmd, cmd.params) ? '⏳' : '▶'} Run
                  </button>
                </div>
                {cmd.params && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {cmd.params.map(p => (
                      <input key={p} placeholder={p} value={paramValues[p] || ''}
                        onChange={e => setParamValues(prev => ({ ...prev, [p]: e.target.value }))}
                        style={{ flex: '1 1 120px', minWidth: 100, padding: '6px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #d1d5db' }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Output Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="card" style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontWeight: 600 }}>Output</h3>
              {output && <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setOutput('')}>Clear</button>}
            </div>
            {lastCommand && (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                <code style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: 4 }}>$ {lastCommand}</code>
                {company && selectedEnv && (
                  <span style={{ marginLeft: 8 }}>
                    <span style={{ color: '#374151', fontWeight: 500 }}>{company.name}</span> {envBadge(selectedEnv)}
                  </span>
                )}
              </div>
            )}
            <pre style={{
              background: '#0f172a', color: '#e2e8f0', padding: 16, borderRadius: 8,
              fontSize: 12, lineHeight: 1.6, minHeight: 300, maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap'
            }}>{output || 'Select a company & environment, then run a command...'}</pre>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontWeight: 600 }}>History ({history.length})</h3>
                <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setHistory([])}>Clear</button>
              </div>
              <div style={{ maxHeight: 220, overflow: 'auto' }}>
                {history.map((h, i) => (
                  <div key={i} onClick={() => { setOutput(h.output); setLastCommand(h.command); }}
                    style={{
                      padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8
                    }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <code style={{ color: '#7c3aed', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.command}</code>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>{h.company} • {h.env?.toUpperCase()}</span>
                    </div>
                    <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
